import { DocumentItemTypeEnum } from '@domain';

/**
 * Tamanho e cor próprios de um título, subtítulo ou texto (configs `textSize` e
 * `textColor` do item). Espelho do `TextItemStyle` do PdfEngine e do `textItemStyle` do
 * pdf-renderer: fora da faixa ou do formato, vale o padrão do tipo. Ausente => padrão;
 * `null` no update-config apaga a chave (a API mescla).
 */

/** Faixa aceita pelo PDF, em pt. */
export const MIN_ITEM_TEXT_SIZE_PT = 6;
export const MAX_ITEM_TEXT_SIZE_PT = 72;

/** Arredonda para meio ponto (10,5 sim; 10,37 não) e prende na faixa do PDF. */
export function clampTextSize(pt: number): number {
    const rounded = Math.round(pt * 2) / 2;
    return Math.min(MAX_ITEM_TEXT_SIZE_PT, Math.max(MIN_ITEM_TEXT_SIZE_PT, rounded));
}

/** O que foi digitado no campo de tamanho ("10,5", "10.5", "14"), ou null se não é número. */
export function parseSizeInput(text: string): number | null {
    const value = Number(text.trim().replace(',', '.'));
    return text.trim() !== '' && Number.isFinite(value) ? clampTextSize(value) : null;
}

/** − e +: passo de um ponto inteiro a partir do valor mostrado (13,5 + 1 = 14; 13,5 - 1 = 13). */
export function stepTextSize(current: number, direction: 1 | -1): number {
    const next = direction > 0 ? Math.floor(current) + 1 : Math.ceil(current) - 1;
    return clampTextSize(next);
}

export interface TextColorChoice {
    hex: string;
    labelKey: string;
}

/** Paleta do menu. A mesma do app, na mesma ordem. */
export const TEXT_COLOR_CHOICES: readonly TextColorChoice[] = [
    { hex: '#141515', labelKey: 'document.edit.textCard.colorBlack' },
    { hex: '#4b5563', labelKey: 'document.edit.textCard.colorGraphite' },
    { hex: '#9ca3af', labelKey: 'document.edit.textCard.colorGray' },
    { hex: '#1d5fb8', labelKey: 'document.edit.textCard.colorBlue' },
    { hex: '#2f80ed', labelKey: 'document.edit.textCard.colorLightBlue' },
    { hex: '#5b3fbf', labelKey: 'document.edit.textCard.colorPurple' },
    { hex: '#1e7a46', labelKey: 'document.edit.textCard.colorGreen' },
    { hex: '#15be78', labelKey: 'document.edit.textCard.colorLightGreen' },
    { hex: '#b4561a', labelKey: 'document.edit.textCard.colorOrange' },
    { hex: '#f9ab10', labelKey: 'document.edit.textCard.colorYellow' },
    { hex: '#b42323', labelKey: 'document.edit.textCard.colorRed' },
    { hex: '#e04f4f', labelKey: 'document.edit.textCard.colorLightRed' },
];

const HEX_COLOR = /^#[0-9a-fA-F]{6}$/;

/** Tamanho próprio válido, ou null (segue o padrão do tipo). */
export function parseItemTextSize(raw: unknown): number | null {
    return typeof raw === 'number' && raw >= MIN_ITEM_TEXT_SIZE_PT && raw <= MAX_ITEM_TEXT_SIZE_PT
        ? raw
        : null;
}

/** Cor própria válida em minúsculas, ou null (segue a cor padrão do tipo). */
export function parseItemTextColor(raw: unknown): string | null {
    if (typeof raw !== 'string') return null;
    const color = raw.trim();
    return HEX_COLOR.test(color) ? color.toLowerCase() : null;
}

/**
 * Tamanho padrão do tipo, em pt: título 21 (28px no PDF), subtítulo 13,5 (18px), tabela 11
 * e o resto = tamanho do texto do documento (12, padrão ABNT, quando ausente).
 */
export function defaultTextSizePt(
    type: DocumentItemTypeEnum | undefined,
    documentFontSize?: number
): number {
    if (type === DocumentItemTypeEnum.TITLE) return 21;
    if (type === DocumentItemTypeEnum.SUBTITLE) return 13.5;
    if (type === DocumentItemTypeEnum.TABLE) return 11;
    return documentFontSize ?? 12;
}

/** Para as prévias: a var --item-size do item em rem, ou null (padrão). */
export function itemTextSizeRem(configs: { textSize?: unknown } | null | undefined): string | null {
    const size = parseItemTextSize(configs?.textSize);
    return size === null ? null : ptToRem(size);
}

/** Para as prévias: a var --item-color do item, ou null (cor padrão). */
export function itemTextColor(configs: { textColor?: unknown } | null | undefined): string | null {
    return parseItemTextColor(configs?.textColor);
}

/** pt => rem do editor: 12pt = 1rem de desenho, a mesma conta do `textSize` do edit.page. */
export function ptToRem(pt: number): string {
    return `${Math.round((pt / 12) * 10000) / 10000}rem`;
}

/** "13,5" em pt, es, fr e de; "13.5" em inglês. */
export function formatPt(pt: number, lang: string | undefined): string {
    const text = String(pt);
    return lang?.startsWith('en') ? text : text.replace('.', ',');
}
